import React, { useCallback, useRef } from "react";
import { useAppContext } from "../app_context";
import { DESKTOP_SIDEBAR_COLLAPSED_WIDTH } from "../common/components/header/header_styled_components";
import { clampSidebarWidth, shouldCollapseAtWidth } from "../common/utils/sidebar_width";

// Restores the CSS var to the stored (expanded) width without touching the stored width
// itself, so a later re-open starts from the same place. Shared by the drag-end-while-
// collapsed path and the double-click toggle so the two cannot drift apart.
const restoreStoredWidthVar = (storedWidth: number) => {
	document.documentElement.style.setProperty("--app-sidebar-width", `${storedWidth}px`);
};

export const useSidebarResize = () => {
	const { sidebarWidth, setSidebarWidth, isSidebarCollapsed, setIsSidebarCollapsed } =
		useAppContext();
	const frameRef = useRef<number | null>(null);
	const latestWidthRef = useRef(sidebarWidth);
	// Guards against a second pointerdown installing a second onMove/onUp pair while a
	// drag is already in progress (e.g. a second pointer, or a stray event after the
	// first gesture failed to terminate cleanly).
	const isDraggingRef = useRef(false);
	// Holds the active gesture's cleanup so unmount can always tear it down, even if
	// pointerup/pointercancel/blur never fire.
	const cleanupRef = useRef<(() => void) | null>(null);

	const startResize = useCallback(
		(event: React.PointerEvent<HTMLElement>) => {
			if (isDraggingRef.current) return;
			isDraggingRef.current = true;

			event.preventDefault();
			const startX = event.clientX;
			// Dragging starts from whatever is on screen: the rail's own width when collapsed,
			// the stored panel width when expanded. That makes the two directions symmetric —
			// drag the rail right to open it, drag the panel left to collapse it.
			const startWidth = isSidebarCollapsed
				? DESKTOP_SIDEBAR_COLLAPSED_WIDTH
				: clampSidebarWidth(sidebarWidth);
			latestWidthRef.current = clampSidebarWidth(startWidth);
			document.documentElement.classList.add("is-resizing");

			// Crossing the threshold swaps the rail and the panel, which unmounts the handle the
			// gesture started on. The listeners therefore live on the document, not on the handle,
			// and pointer capture is deliberately not used — otherwise the drag would die mid-way.
			let isCollapsed = isSidebarCollapsed;
			let isDone = false;

			const onMove = (moveEvent: PointerEvent) => {
				// Chrome can swallow pointerup for a mouse button (e.g. a right-click opening a
				// context menu mid-drag) without ever firing pointercancel. If no button is held
				// anymore, the gesture is over even though we never got told — terminate now.
				if (moveEvent.buttons === 0) {
					onUp();
					return;
				}

				const rawWidth = startWidth + (moveEvent.clientX - startX);
				const nextCollapsed = shouldCollapseAtWidth(rawWidth);
				if (nextCollapsed !== isCollapsed) {
					isCollapsed = nextCollapsed;
					setIsSidebarCollapsed(nextCollapsed);
				}
				if (nextCollapsed) return;

				latestWidthRef.current = clampSidebarWidth(rawWidth);
				if (frameRef.current == null) {
					frameRef.current = requestAnimationFrame(() => {
						frameRef.current = null;
						document.documentElement.style.setProperty(
							"--app-sidebar-width",
							`${latestWidthRef.current}px`,
						);
					});
				}
			};

			const onUp = () => {
				// Safe to call more than once: pointerup, pointercancel, buttons===0 inside
				// onMove, and window blur can each try to terminate the same gesture.
				if (isDone) return;
				isDone = true;

				document.removeEventListener("pointermove", onMove);
				document.removeEventListener("pointerup", onUp);
				document.removeEventListener("pointercancel", onUp);
				window.removeEventListener("blur", onUp);
				if (frameRef.current != null) {
					cancelAnimationFrame(frameRef.current);
					frameRef.current = null;
				}
				document.documentElement.classList.remove("is-resizing");
				isDraggingRef.current = false;
				cleanupRef.current = null;
				if (isCollapsed) {
					// Keep the stored expanded width untouched so re-opening restores it.
					restoreStoredWidthVar(sidebarWidth);
					return;
				}
				setSidebarWidth(latestWidthRef.current);
			};

			document.addEventListener("pointermove", onMove);
			document.addEventListener("pointerup", onUp);
			document.addEventListener("pointercancel", onUp);
			window.addEventListener("blur", onUp);
			cleanupRef.current = onUp;
		},
		[sidebarWidth, setSidebarWidth, isSidebarCollapsed, setIsSidebarCollapsed],
	);

	// Unmount safety net: if the hook's owner goes away mid-drag (route change, etc.),
	// make sure the document-level listeners, pending frame and is-resizing class don't
	// outlive it.
	React.useEffect(() => {
		return () => {
			cleanupRef.current?.();
			if (frameRef.current != null) {
				cancelAnimationFrame(frameRef.current);
				frameRef.current = null;
			}
			document.documentElement.classList.remove("is-resizing");
		};
	}, []);

	// Double-clicking the handle flips to the other shape, from either side.
	const toggleWidth = useCallback(() => {
		restoreStoredWidthVar(sidebarWidth);
		setIsSidebarCollapsed(!isSidebarCollapsed);
	}, [sidebarWidth, isSidebarCollapsed, setIsSidebarCollapsed]);

	return { startResize, toggleWidth };
};
